import React, {useEffect, useRef, useState} from 'react';
import styled from "styled-components";

const DialogWrapper = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: var(--overlay-background-color);
	opacity: ${props => props.$isOpen ? 1 : 0};
	pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
	transition: opacity 0.3s ease-in-out;
	overflow: hidden;
`;

const DialogContent = styled.div`
	position: relative;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 2rem;
	width: clamp(200px, 70%, 750px);
	height: fit-content;
	min-height: 100px;
	max-height: 90%;
	padding: 3rem;
	border-radius: 0.5rem;
	box-shadow: 7px 7px 0 0 var(--primary-color);
	color: var(--text-color);
	background: var(--background-color);
	transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.7)'};
	transition: transform 0.3s ease-in-out;
`;

const CloseDialogButton = styled.button`
	position: absolute;
	top: 1rem;
	right: 1rem;
	width: 1.5rem;
	height: 1.5rem;
	border: none;
	outline: none;
	background: none;
	cursor: pointer;
	
	&:hover span {
		background: var(--subtext-color);
	}
	
	span {
		position: absolute;
		top: 50%;
		left: 50%;
		width: 100%;
		height: 2px;
		background: var(--text-color);
		transform: translate(-50%, -50%) rotate(45deg);
		transition: all 0.3s ease-in-out;
		
		&:nth-child(2) {
			transform: translate(-50%, -50%) rotate(-45deg);
		}
	}
`;

export default function Dialog({ children, className, style, isOpen, onClose }) {
	const [isVisible, setIsVisible] = useState(false);
	const [content, setContent] = useState(null);
	const dialogWrapperRef = useRef(null);
	const dialogCloseButtonRef = useRef(null);

	useEffect(() => {
        let timeout;

		if (isOpen) {
            // Set content immediately but delay showing it (for animation to start)
			setContent(children);
            requestAnimationFrame(() => setIsVisible(true));
            // setTimeout(() => setIsVisible(true), 0);
		} else {
            // Hide content immediately but delay unsetting it (for animation to end)
            setIsVisible(false);
			timeout = setTimeout(() => setContent(null), 300);
		}

        return () => clearTimeout(timeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- 'children' should not trigger the hook!
	}, [isOpen]);

	function closeDialog(event) {
		if (event.target === dialogWrapperRef?.current || event.target === dialogCloseButtonRef?.current || event.target.parentNode === dialogCloseButtonRef?.current) {
			event.stopPropagation();
			onClose();
		}
	}

	return (
		<DialogWrapper
			style={style}
			className={className}
			ref={dialogWrapperRef}
			onClick={closeDialog}
			$isOpen={isVisible}
		>
            {content && (
                <DialogContent $isOpen={isVisible}>
                    <CloseDialogButton type="button" ref={dialogCloseButtonRef} onClick={closeDialog}>
                        <span/><span/>
                    </CloseDialogButton>
                    {content}
                </DialogContent>
            )}
		</DialogWrapper>
	);
}
